import { BaseCMI } from "../../common/base_cmi";
import { Activity, ActivityObjective } from "./activity";
import { Scorm2004ValidationError } from "../../../exceptions/scorm2004_exceptions";
import { scorm2004_errors } from "../../../constants/error_codes";
import { SuccessStatus, CompletionStatus } from "../../../constants/enums";
import { getDurationAsSeconds } from "../../../utilities";
import { scorm2004_regex } from "../../../constants/regex";

/**
 * Enum for rule condition operators
 */
export enum RuleConditionOperator {
  NOT = "not",
  AND = "and",
  OR = "or",
}

/**
 * Enum for rule condition types
 */
export enum RuleConditionType {
  SATISFIED = "satisfied",
  OBJECTIVE_SATISFIED = "objectiveSatisfied",
  OBJECTIVE_STATUS_KNOWN = "objectiveStatusKnown",
  OBJECTIVE_MEASURE_KNOWN = "objectiveMeasureKnown",
  OBJECTIVE_MEASURE_GREATER_THAN = "objectiveMeasureGreaterThan",
  OBJECTIVE_MEASURE_LESS_THAN = "objectiveMeasureLessThan",
  COMPLETED = "completed",
  ACTIVITY_COMPLETED = "activityCompleted",
  PROGRESS_KNOWN = "progressKnown",
  ACTIVITY_PROGRESS_KNOWN = "activityProgressKnown",
  ATTEMPTED = "attempted",
  ATTEMPT_LIMIT_EXCEEDED = "attemptLimitExceeded",
  TIME_LIMIT_EXCEEDED = "timeLimitExceeded",
  OUTSIDE_AVAILABLE_TIME_RANGE = "outsideAvailableTimeRange",
  ALWAYS = "always",
  NEVER = "never",
}

/**
 * Enum for rule action types
 */
export enum RuleActionType {
  SKIP = "skip",
  DISABLED = "disabled",
  HIDE_FROM_CHOICE = "hiddenFromChoice",
  STOP_FORWARD_TRAVERSAL = "stopForwardTraversal",
  EXIT_PARENT = "exitParent",
  EXIT_ALL = "exitAll",
  RETRY = "retry",
  RETRY_ALL = "retryAll",
  CONTINUE = "continue",
  PREVIOUS = "previous",
  EXIT = "exit",
}

export type RuleConditionEvaluation = boolean | "unknown";

const OBJECTIVE_TRACKING_CONDITIONS = new Set<RuleConditionType>([
  RuleConditionType.SATISFIED,
  RuleConditionType.OBJECTIVE_SATISFIED,
  RuleConditionType.OBJECTIVE_STATUS_KNOWN,
  RuleConditionType.OBJECTIVE_MEASURE_KNOWN,
  RuleConditionType.OBJECTIVE_MEASURE_GREATER_THAN,
  RuleConditionType.OBJECTIVE_MEASURE_LESS_THAN,
]);

export function kleeneNot(value: RuleConditionEvaluation): RuleConditionEvaluation {
  if (value === "unknown") {
    return "unknown";
  }

  return !value;
}

export function kleeneAnd(values: RuleConditionEvaluation[]): RuleConditionEvaluation {
  let hasUnknown = false;

  for (const value of values) {
    if (value === false) {
      return false;
    }
    if (value === "unknown") {
      hasUnknown = true;
    }
  }

  return hasUnknown ? "unknown" : true;
}

export function kleeneOr(values: RuleConditionEvaluation[]): RuleConditionEvaluation {
  let hasUnknown = false;

  for (const value of values) {
    if (value === true) {
      return true;
    }
    if (value === "unknown") {
      hasUnknown = true;
    }
  }

  return hasUnknown ? "unknown" : false;
}

export function combineRuleConditionResults(
  values: RuleConditionEvaluation[],
  conditionCombination: string | RuleConditionOperator,
): RuleConditionEvaluation {
  if (values.length === 0) {
    return false;
  }

  if (conditionCombination === "all" || conditionCombination === RuleConditionOperator.AND) {
    return kleeneAnd(values);
  }

  if (conditionCombination === "any" || conditionCombination === RuleConditionOperator.OR) {
    return kleeneOr(values);
  }

  return false;
}

/**
 * Class representing a sequencing rule condition
 */
export class RuleCondition extends BaseCMI {
  private _condition: RuleConditionType = RuleConditionType.ALWAYS;
  private _operator: RuleConditionOperator | null = null;
  private _parameters: Map<string, any> = new Map();
  private _referencedObjective: string | null = null;
  // Optional, overridable provider for current time (LMS may set via SequencingService)
  private static _now: () => Date = () => new Date();
  // Optional, overridable hook for getting elapsed seconds
  private static _getElapsedSecondsHook: ((activity: Activity) => number) | undefined = undefined;

  /**
   * Constructor for RuleCondition
   * @param {RuleConditionType} condition - The condition type
   * @param {RuleConditionOperator | null} operator - The operator (null for no operator)
   * @param {Map<string, any>} parameters - Additional parameters for the condition
   */
  constructor(
    condition: RuleConditionType = RuleConditionType.ALWAYS,
    operator: RuleConditionOperator | null = null,
    parameters: Map<string, any> = new Map(),
  ) {
    super("ruleCondition");
    this._condition = condition;
    this._operator = operator;
    this._parameters = parameters;
  }

  /**
   * Allow integrators to override the clock used for time-based rules.
   */
  public static setNowProvider(now: () => Date) {
    if (typeof now === "function") {
      RuleCondition._now = now;
    }
  }

  /**
   * Allow integrators to set an elapsed seconds hook for time limit calculations
   */
  public static setElapsedSecondsHook(hook: ((activity: Activity) => number) | undefined) {
    RuleCondition._getElapsedSecondsHook = hook;
  }

  /**
   * Called when the API needs to be reset
   */
  reset() {
    this._initialized = false;
    this._condition = RuleConditionType.ALWAYS;
    this._operator = null;
    this._parameters = new Map();
  }

  /**
   * Getter for condition
   * @return {RuleConditionType}
   */
  get condition(): RuleConditionType {
    return this._condition;
  }

  /**
   * Setter for condition
   * @param {RuleConditionType} condition
   */
  set condition(condition: RuleConditionType) {
    this._condition = condition;
  }

  /**
   * Getter for operator
   * @return {RuleConditionOperator | null}
   */
  get operator(): RuleConditionOperator | null {
    return this._operator;
  }

  /**
   * Setter for operator
   * @param {RuleConditionOperator | null} operator
   */
  set operator(operator: RuleConditionOperator | null) {
    this._operator = operator;
  }

  /**
   * Getter for parameters
   * @return {Map<string, any>}
   */
  get parameters(): Map<string, any> {
    return this._parameters;
  }

  /**
   * Setter for parameters
   * @param {Map<string, any>} parameters
   */
  set parameters(parameters: Map<string, any>) {
    this._parameters = parameters;
  }

  get referencedObjective(): string | null {
    return this._referencedObjective;
  }

  set referencedObjective(objectiveId: string | null) {
    this._referencedObjective = objectiveId;
  }

  private resolveReferencedObjective(activity: Activity): ActivityObjective | null {
    if (!this._referencedObjective) {
      return null;
    }
    if (activity.primaryObjective?.id === this._referencedObjective) {
      return activity.primaryObjective;
    }
    const objectives = activity.objectives || [];
    return objectives.find((obj) => obj.id === this._referencedObjective) || null;
  }

  /**
   * Evaluate the condition for an activity
   * @param {Activity} activity - The activity to evaluate the condition for
   * @return {RuleConditionEvaluation} - True, false, or unknown per SCORM 2004 4th Ed.
   */
  evaluate(activity: Activity): RuleConditionEvaluation {
    let result: RuleConditionEvaluation;
    const hasReferencedObjective = this._referencedObjective !== null;
    const referencedObjective = this.resolveReferencedObjective(activity);

    // @spec SCORM 2004 SN: objective tracking information for an activity with
    // deliveryControls.tracked=false is unavailable to its sequencing rules.
    // The SM.1 useCurrentAttempt* flags govern whether child tracking participates in
    // parent rollup; they do not hide an activity's own mapped/preserved tracking from
    // that activity's UP.2.1 precondition evaluation.
    // @spec SCORM 2004 4th Ed. TR OB-03b steps 6-7 and SN UP.2.1/SB.2.2
    if (
      (activity.sequencingControls?.tracked === false &&
        OBJECTIVE_TRACKING_CONDITIONS.has(this._condition))
    ) {
      result = "unknown";
    } else
      switch (this._condition) {
        case RuleConditionType.SATISFIED:
        case RuleConditionType.OBJECTIVE_SATISFIED:
          if (hasReferencedObjective && !referencedObjective) {
            result = false;
          } else if (referencedObjective) {
            result =
              referencedObjective.satisfiedStatusKnown || referencedObjective.progressStatus
                ? referencedObjective.satisfiedStatus === true
                : "unknown";
          } else if (activity.objectiveSatisfiedStatusKnown) {
            result = activity.objectiveSatisfiedStatus === true;
          } else if (
            !activity.primaryObjective &&
            activity.successStatus !== SuccessStatus.UNKNOWN
          ) {
            result = activity.successStatus === SuccessStatus.PASSED;
          } else {
            result = "unknown";
          }
          break;
        case RuleConditionType.OBJECTIVE_STATUS_KNOWN:
          // noinspection PointlessBooleanExpressionJS
          result =
            hasReferencedObjective && !referencedObjective
              ? false
              : referencedObjective
                ? !!referencedObjective.satisfiedStatusKnown
                : !!activity.objectiveSatisfiedStatusKnown;
          break;
        case RuleConditionType.OBJECTIVE_MEASURE_KNOWN:
          // noinspection PointlessBooleanExpressionJS
          result =
            hasReferencedObjective && !referencedObjective
              ? false
              : referencedObjective
                ? !!referencedObjective.measureStatus
                : !!activity.objectiveMeasureStatus;
          break;
        case RuleConditionType.OBJECTIVE_MEASURE_GREATER_THAN: {
          if (hasReferencedObjective && !referencedObjective) {
            result = false;
            break;
          }
          const greaterThanValue = this._parameters.get("threshold") || 0;
          const measureStatus = referencedObjective
            ? referencedObjective.measureStatus
            : activity.objectiveMeasureStatus;
          const measureValue = referencedObjective
            ? referencedObjective.normalizedMeasure
            : activity.objectiveNormalizedMeasure;
          result = measureStatus ? measureValue > greaterThanValue : "unknown";
          break;
        }
        case RuleConditionType.OBJECTIVE_MEASURE_LESS_THAN: {
          if (hasReferencedObjective && !referencedObjective) {
            result = false;
            break;
          }
          const lessThanValue = this._parameters.get("threshold") || 0;
          const measureStatus = referencedObjective
            ? referencedObjective.measureStatus
            : activity.objectiveMeasureStatus;
          const measureValue = referencedObjective
            ? referencedObjective.normalizedMeasure
            : activity.objectiveNormalizedMeasure;
          result = measureStatus ? measureValue < lessThanValue : "unknown";
          break;
        }
        case RuleConditionType.COMPLETED:
        case RuleConditionType.ACTIVITY_COMPLETED:
          // SCORM 2004 4th Edition: When referencedObjective is specified,
          // check the objective's completion status instead of the activity's
          if (hasReferencedObjective && !referencedObjective) {
            result = false;
          } else if (referencedObjective) {
            result =
              referencedObjective.completionStatus === CompletionStatus.UNKNOWN
                ? "unknown"
                : referencedObjective.completionStatus === CompletionStatus.COMPLETED;
          } else if (activity.completionStatus === CompletionStatus.UNKNOWN) {
            result = "unknown";
          } else {
            result = activity.completionStatus === CompletionStatus.COMPLETED;
          }
          break;
        case RuleConditionType.PROGRESS_KNOWN:
        case RuleConditionType.ACTIVITY_PROGRESS_KNOWN:
          // SCORM 2004 4th Edition: When referencedObjective is specified,
          // check the objective's completion status instead of the activity's
          if (hasReferencedObjective && !referencedObjective) {
            result = false;
          } else if (referencedObjective) {
            result = referencedObjective.completionStatus !== CompletionStatus.UNKNOWN;
          } else {
            result = activity.completionStatus !== "unknown";
          }
          break;
        case RuleConditionType.ATTEMPTED:
          result = activity.attemptCount > 0;
          break;
        case RuleConditionType.ATTEMPT_LIMIT_EXCEEDED:
          // Use activity's hasAttemptLimitExceeded() which properly checks
          // if the activity has an attempt limit set
          result = activity.hasAttemptLimitExceeded();
          break;
        case RuleConditionType.TIME_LIMIT_EXCEEDED:
          result = this.evaluateTimeLimitExceeded(activity);
          break;
        case RuleConditionType.OUTSIDE_AVAILABLE_TIME_RANGE:
          result = this.evaluateOutsideAvailableTimeRange(activity);
          break;
        case RuleConditionType.ALWAYS:
          result = true;
          break;
        case RuleConditionType.NEVER:
          result = false;
          break;
        default:
          result = false;
          break;
      }

    if (this._operator === RuleConditionOperator.NOT) {
      result = kleeneNot(result);
    }

    return result;
  }

  /**
   * Evaluate if time limit has been exceeded
   * @param {Activity} activity - The activity to evaluate
   * @return {boolean}
   * @private
   */
  private evaluateTimeLimitExceeded(activity: Activity): boolean {
    // Check timeLimitDuration (primary time limit)
    let limit = activity.timeLimitDuration;

    // Fallback to attemptAbsoluteDurationLimit if timeLimitDuration is not set
    if (!limit && activity.attemptAbsoluteDurationLimit) {
      limit = activity.attemptAbsoluteDurationLimit;
    }

    // No limit means condition is false
    if (!limit) {
      return false;
    }

    // Parse limit to seconds
    const limitSeconds = getDurationAsSeconds(limit, scorm2004_regex.CMITimespan);

    // Invalid or zero limit means condition is false
    if (limitSeconds <= 0) {
      return false;
    }

    let elapsedSeconds = 0;

    // Strategy 1: Use hook if available (preferred for LMS integration)
    if (RuleCondition._getElapsedSecondsHook) {
      try {
        const hookResult = RuleCondition._getElapsedSecondsHook(activity);
        if (typeof hookResult === "number" && !Number.isNaN(hookResult) && hookResult >= 0) {
          elapsedSeconds = hookResult;
        }
      } catch {
        elapsedSeconds = 0;
      }
    }

    // Strategy 2: Try to use attemptExperiencedDuration if hook not available
    if (elapsedSeconds === 0 && activity.attemptExperiencedDuration) {
      const attemptDurationSeconds = getDurationAsSeconds(
        activity.attemptExperiencedDuration,
        scorm2004_regex.CMITimespan,
      );
      if (attemptDurationSeconds > 0) {
        elapsedSeconds = attemptDurationSeconds;
      }
    }

    // Strategy 3: Calculate from attemptAbsoluteStartTime if duration not available
    if (elapsedSeconds === 0 && activity.attemptAbsoluteStartTime) {
      try {
        const start = new Date(activity.attemptAbsoluteStartTime).getTime();
        const nowMs = RuleCondition._now().getTime();

        // Validate timestamps before calculating
        if (!Number.isNaN(start) && !Number.isNaN(nowMs) && nowMs >= start) {
          elapsedSeconds = (nowMs - start) / 1000;
        }
      } catch {
        elapsedSeconds = 0;
      }
    }

    // Time limit is exceeded if elapsed time is strictly greater than limit
    return elapsedSeconds > limitSeconds;
  }

  /**
   * Evaluate if activity is outside available time range
   * @param {Activity} activity - The activity to evaluate
   * @return {boolean}
   * @private
   */
  private evaluateOutsideAvailableTimeRange(activity: Activity): boolean {
    const beginTime = activity.beginTimeLimit;
    const endTime = activity.endTimeLimit;

    if (!beginTime && !endTime) {
      return false;
    }

    const now = RuleCondition._now();

    if (beginTime) {
      const beginDate = new Date(beginTime);
      if (now < beginDate) {
        return true;
      }
    }

    if (endTime) {
      const endDate = new Date(endTime);
      if (now > endDate) {
        return true;
      }
    }

    return false;
  }

  /**
   * Parse ISO 8601 duration to milliseconds
   * Uses the standard getDurationAsSeconds utility which supports full ISO 8601 format
   * including date components (years, months, weeks, days) and time components (hours, minutes, seconds).
   * @param {string} duration - ISO 8601 duration string (e.g., "PT1H30M", "P1D", "P1Y2M3DT4H5M6S")
   * @return {number} - Duration in milliseconds
   * @private
   */
  private parseISO8601Duration(duration: string): number {
    // Use the standard utility function which handles full ISO 8601 duration format
    // including years (Y), months (M), weeks (W), days (D), hours (H), minutes (M), and seconds (S)
    const seconds = getDurationAsSeconds(duration, scorm2004_regex.CMITimespan);
    // Convert seconds to milliseconds
    return seconds * 1000;
  }

  /**
   * toJSON for RuleCondition
   * @return {object}
   */
  toJSON(): object {
    this.jsonString = true;
    const result = {
      condition: this._condition,
      operator: this._operator,
      parameters: Object.fromEntries(this._parameters),
    };
    this.jsonString = false;
    return result;
  }
}

/**
 * Class representing a sequencing rule
 */
export class SequencingRule extends BaseCMI {
  private _conditions: RuleCondition[] = [];
  private _action: RuleActionType = RuleActionType.SKIP;
  private _conditionCombination: string | RuleConditionOperator = RuleConditionOperator.AND;

  /**
   * Constructor for SequencingRule
   * @param {RuleActionType} action - The action to take when the rule conditions are met
   * @param {string | RuleConditionOperator} conditionCombination - How to combine multiple conditions ("all"/"and" or "any"/"or")
   */
  constructor(
    action: RuleActionType = RuleActionType.SKIP,
    conditionCombination: string | RuleConditionOperator = RuleConditionOperator.AND,
  ) {
    super("sequencingRule");
    this._action = action;
    this._conditionCombination = conditionCombination;
  }

  /**
   * Called when the API needs to be reset
   */
  reset() {
    this._initialized = false;
    this._conditions = [];
    this._action = RuleActionType.SKIP;
    this._conditionCombination = RuleConditionOperator.AND;
  }

  /**
   * Getter for conditions
   * @return {RuleCondition[]}
   */
  get conditions(): RuleCondition[] {
    return this._conditions;
  }

  /**
   * Add a condition to the rule
   * @param {RuleCondition} condition - The condition to add
   */
  addCondition(condition: RuleCondition): void {
    // noinspection SuspiciousTypeOfGuard
    if (!(condition instanceof RuleCondition)) {
      throw new Scorm2004ValidationError(
        this._cmi_element + ".conditions",
        scorm2004_errors.TYPE_MISMATCH as number,
      );
    }
    // Check if the condition is already in the array
    if (!this._conditions.includes(condition)) {
      this._conditions.push(condition);
    }
  }

  /**
   * Remove a condition from the rule
   * @param {RuleCondition} condition - The condition to remove
   * @return {boolean} - True if the condition was removed, false otherwise
   */
  removeCondition(condition: RuleCondition): boolean {
    // noinspection SuspiciousTypeOfGuard
    if (!(condition instanceof RuleCondition)) {
      throw new Scorm2004ValidationError(
        this._cmi_element + ".conditions",
        scorm2004_errors.TYPE_MISMATCH as number,
      );
    }
    const index = this._conditions.indexOf(condition);
    if (index !== -1) {
      this._conditions.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Getter for action
   * @return {RuleActionType}
   */
  get action(): RuleActionType {
    return this._action;
  }

  /**
   * Setter for action
   * @param {RuleActionType} action
   */
  set action(action: RuleActionType) {
    this._action = action;
  }

  /**
   * Getter for conditionCombination
   * @return {string | RuleConditionOperator}
   */
  get conditionCombination(): string | RuleConditionOperator {
    return this._conditionCombination;
  }

  /**
   * Setter for conditionCombination
   * @param {string | RuleConditionOperator} conditionCombination
   */
  set conditionCombination(conditionCombination: string | RuleConditionOperator) {
    this._conditionCombination = conditionCombination;
  }

  /**
   * Evaluate the rule for an activity
   * @param {Activity} activity - The activity to evaluate the rule for
   * @return {boolean} - True if the rule conditions are met, false otherwise
   */
  evaluate(activity: Activity): boolean {
    return (
      combineRuleConditionResults(
        this._conditions.map((condition) => condition.evaluate(activity)),
        this._conditionCombination,
      ) === true
    );
  }

  /**
   * toJSON for SequencingRule
   * @return {object}
   */
  toJSON(): object {
    this.jsonString = true;
    const result = {
      conditions: this._conditions,
      action: this._action,
      conditionCombination: this._conditionCombination,
    };
    this.jsonString = false;
    return result;
  }
}

/**
 * Class representing a collection of sequencing rules
 */
export class SequencingRules extends BaseCMI {
  private _preConditionRules: SequencingRule[] = [];
  private _exitConditionRules: SequencingRule[] = [];
  private _postConditionRules: SequencingRule[] = [];

  /**
   * Constructor for SequencingRules
   */
  constructor() {
    super("sequencingRules");
  }

  /**
   * Called when the API needs to be reset
   */
  reset() {
    this._initialized = false;
    this._preConditionRules = [];
    this._exitConditionRules = [];
    this._postConditionRules = [];
  }

  /**
   * Getter for preConditionRules
   * @return {SequencingRule[]}
   */
  get preConditionRules(): SequencingRule[] {
    return this._preConditionRules;
  }

  /**
   * Add a pre-condition rule
   * @param {SequencingRule} rule - The rule to add
   */
  addPreConditionRule(rule: SequencingRule): void {
    // noinspection SuspiciousTypeOfGuard
    if (!(rule instanceof SequencingRule)) {
      throw new Scorm2004ValidationError(
        this._cmi_element + ".preConditionRules",
        scorm2004_errors.TYPE_MISMATCH as number,
      );
    }
    this._preConditionRules.push(rule);
  }

  /**
   * Getter for exitConditionRules
   * @return {SequencingRule[]}
   */
  get exitConditionRules(): SequencingRule[] {
    return this._exitConditionRules;
  }

  /**
   * Add an exit condition rule
   * @param {SequencingRule} rule - The rule to add
   */
  addExitConditionRule(rule: SequencingRule): void {
    // noinspection SuspiciousTypeOfGuard
    if (!(rule instanceof SequencingRule)) {
      throw new Scorm2004ValidationError(
        this._cmi_element + ".exitConditionRules",
        scorm2004_errors.TYPE_MISMATCH as number,
      );
    }
    this._exitConditionRules.push(rule);
  }

  /**
   * Getter for postConditionRules
   * @return {SequencingRule[]}
   */
  get postConditionRules(): SequencingRule[] {
    return this._postConditionRules;
  }

  /**
   * Add a post-condition rule
   * @param {SequencingRule} rule - The rule to add
   */
  addPostConditionRule(rule: SequencingRule): void {
    // noinspection SuspiciousTypeOfGuard
    if (!(rule instanceof SequencingRule)) {
      throw new Scorm2004ValidationError(
        this._cmi_element + ".postConditionRules",
        scorm2004_errors.TYPE_MISMATCH as number,
      );
    }
    this._postConditionRules.push(rule);
  }

  /**
   * Evaluate pre-condition rules for an activity
   * @param {Activity} activity - The activity to evaluate the rules for
   * @return {RuleActionType | null} - The action to take, or null if no rules are met
   */
  evaluatePreConditionRules(activity: Activity): RuleActionType | null {
    for (const rule of this._preConditionRules) {
      if (rule.evaluate(activity)) {
        return rule.action;
      }
    }
    return null;
  }

  /**
   * Evaluate exit condition rules for an activity
   * @param {Activity} activity - The activity to evaluate the rules for
   * @return {RuleActionType | null} - The action to take, or null if no rules are met
   */
  evaluateExitConditionRules(activity: Activity): RuleActionType | null {
    for (const rule of this._exitConditionRules) {
      if (rule.evaluate(activity)) {
        return rule.action;
      }
    }
    return null;
  }

  /**
   * Evaluate post-condition rules for an activity
   * @param {Activity} activity - The activity to evaluate the rules for
   * @return {RuleActionType | null} - The action to take, or null if no rules are met
   */
  evaluatePostConditionRules(activity: Activity): RuleActionType | null {
    for (const rule of this._postConditionRules) {
      if (rule.evaluate(activity)) {
        return rule.action;
      }
    }
    return null;
  }

  /**
   * toJSON for SequencingRules
   * @return {object}
   */
  toJSON(): object {
    this.jsonString = true;
    const result = {
      preConditionRules: this._preConditionRules,
      exitConditionRules: this._exitConditionRules,
      postConditionRules: this._postConditionRules,
    };
    this.jsonString = false;
    return result;
  }
}
